import React, { useEffect, useState } from 'react';
import { Button, Spin, Form, Modal, Tabs } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { TextLabelRecognitionProps, Template } from './types';
import { useTextLabelApi } from './useTextLabelApi';
import TaskTable from './TaskTable';
import TemplateTable from './TemplateTable';
import TaskModal from './TaskModal';
import TemplateModal from './TemplateModal';

type ViewType = 'task' | 'template';

const TextLabelRecognition: React.FC<TextLabelRecognitionProps> = ({ username, isAdmin, isLoggedIn }) => {
  const [selectedProject, setSelectedProject] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateForm] = Form.useForm();
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [form] = Form.useForm();
  const [activeView, setActiveView] = useState<ViewType>('task');

  const {
    projects,
    templates,
    tasks,
    total,
    page,
    pageSize,
    loading,
    submitLoading,
    fetchProjects,
    fetchTemplates,
    fetchTasks,
    handleDownload,
    handleTaskSubmit,
    handleTemplateSubmit,
    handleDeleteTemplate
  } = useTextLabelApi(username);

  const handlePageChange = (p: number, ps: number) => {
    fetchTasks(p, ps);
  };

  const handleTaskModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      const fileList = values.file;
      if (!fileList || !fileList.length || !fileList[0].originFileObj) {
        return;
      }
      
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj);
      formData.append('project_name', values.project_name);
      formData.append('template_name', values.template_name);
      formData.append('username', username);
      
      const success = await handleTaskSubmit(formData);
      if (success) {
        setModalOpen(false);
        form.resetFields();
      }
    } catch (e) {
      console.log('表单校验失败:', e);
    }
  };

  const handleTemplateModalSubmit = async () => {
    try {
      const values = await templateForm.validateFields();
      const success = await handleTemplateSubmit(values, editingTemplate);
      if (success) {
        setTemplateModalOpen(false);
        setEditingTemplate(null);
        templateForm.resetFields();
      }
    } catch (e) {
      console.log('表单校验失败:', e);
    }
  };

  const handleEditTemplate = (template: Template) => {
    console.log('编辑模板数据:', template);
    setEditingTemplate(template);
    
    // 处理examples数据格式
    let examplesArray = [{ input: '', output: '' }];
    if (template.examples) {
      try {
        let inputTexts: string[] = [];
        if (typeof template.examples.input === 'string') {
          try {
            const inputObj = JSON.parse(template.examples.input);
            if (typeof inputObj === 'object') {
              inputTexts = Object.keys(inputObj).map(key => inputObj[key]);
            } else {
              inputTexts = [template.examples.input];
            }
          } catch (e) {
            inputTexts = [template.examples.input];
            if (template.examples.input && template.examples.input.includes('\n')) {
              inputTexts = template.examples.input.split('\n').filter((text: string) => text.trim());
            }
          }
        }
        
        if (typeof template.examples.output === 'string') {
          const outputObj = JSON.parse(template.examples.output);
          if (outputObj.tag) {
            const tagKeys = Object.keys(outputObj.tag);
            examplesArray = tagKeys.map((key, index) => ({
              input: inputTexts[index] || '',
              output: outputObj.tag[key]
            }));
          }
        }
        
        if (examplesArray.length === 0) {
          examplesArray = [{ input: '', output: '' }];
        }
      } catch (e) {
        console.log('解析examples失败:', e);
        examplesArray = [{ input: '', output: '' }];
      }
    }
    
    templateForm.setFieldsValue({
      project_name: template.project_name,
      template_name: template.template_name,
      background: template.background,
      stance: template.stance,
      emotion_judgement: template.emotion_judgement,
      label_levels: template.label_levels,
      label_judgement: typeof template.label_judgement === 'object' ? JSON.stringify(template.label_judgement) : (template.label_judgement || ''),
      label_system: Array.isArray(template.label_system) ? JSON.stringify(template.label_system) : (template.label_system || ''),
      examples: examplesArray
    });
    
    setTemplateModalOpen(true);
  };

  const handleDeleteTemplateConfirm = async (template: Template) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除模板"${template.template_name}"吗？`,
      onOk: async () => {
        await handleDeleteTemplate(template);
      },
    });
  };

  const handleTemplateModalCancel = () => {
    setTemplateModalOpen(false);
    setEditingTemplate(null);
    templateForm.resetFields();
  };

  const handleTaskModalCancel = () => {
    setModalOpen(false);
    form.resetFields();
  };

  useEffect(() => {
    console.log('TextLabelRecognition useEffect triggered:', { isLoggedIn, username });
    if (isLoggedIn && username) {
      console.log('Fetching data for user:', username);
      fetchProjects();
      fetchTemplates();
      fetchTasks(page, pageSize);
    } else {
      console.log('Not fetching data - isLoggedIn:', isLoggedIn, 'username:', username);
    }
  }, [isLoggedIn, username]);

  const handleViewChange = (key: string) => {
    setActiveView(key as ViewType);
  };

  const renderTaskView = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          新增文本标签识别任务
        </Button>
        <Button onClick={() => fetchTasks(page, pageSize)}>刷新数据</Button>
      </div>
      <div>
        <h3>任务列表</h3>
        <Spin spinning={loading} tip="加载中...">
          <TaskTable
            tasks={tasks}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onDownload={handleDownload}
          />
        </Spin>
      </div>
    </div>
  );

  const renderTemplateView = () => (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setTemplateModalOpen(true)}>
          新增模板
        </Button>
      </div>
      <div>
        <h3>模板管理</h3>
        <TemplateTable
          templates={templates}
          onEdit={handleEditTemplate}
          onDelete={handleDeleteTemplateConfirm}
        />
      </div>
    </div>
  );

  return (
    <>
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <Tabs
            activeKey={activeView}
            onChange={handleViewChange}
            items={[
              {
                key: 'task',
                label: '文本标签识别任务',
              },
              {
                key: 'template',
                label: '模板管理',
              },
            ]}
          />
        </div>
      )}
      
      {isAdmin ? (
        activeView === 'task' ? renderTaskView() : renderTemplateView()
      ) : (
        renderTaskView()
      )}

      <TaskModal
        open={modalOpen}
        loading={submitLoading}
        projects={projects}
        templates={templates}
        selectedProject={selectedProject}
        form={form}
        onSubmit={handleTaskModalSubmit}
        onCancel={handleTaskModalCancel}
        onProjectChange={setSelectedProject}
      />

      <TemplateModal
        open={templateModalOpen}
        loading={submitLoading}
        editingTemplate={editingTemplate}
        form={templateForm}
        onSubmit={handleTemplateModalSubmit}
        onCancel={handleTemplateModalCancel}
      />    </>
  );
};

export default TextLabelRecognition;
